<script lang="ts">
	import type { ClassValue } from "svelte/elements";
	import type { Media } from "@core/schema/Media.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";

	const {
		source,
		alt,
		width = 24,
		height = 24,
		class: customClass,
		useCurrentColor = true,
	}: {
		source: Media;
		alt: string;
		width?: number;
		height?: number;
		class?: ClassValue;
		useCurrentColor?: boolean;
	} = $props();

	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	const imageUrl = $derived(getUploadedFilePath(source, FILE_BUCKET_URL_MAP));

	const isSvg = $derived(() => {
		if (source.type === "image/svg+xml" || source.type === "image/svg") {
			return true;
		}

		if (source.path.toLowerCase().endsWith(".svg")) {
			return true;
		}

		if (imageUrl.toLowerCase().includes(".svg")) {
			return true;
		}

		return false;
	});

	let svgContent = $state("");
	let isLoaded = $state(false);

	const isExternalUrl = $derived(() => {
		try {
			const url = new URL(imageUrl);
			return url.origin !== window.location.origin;
		} catch {
			return false;
		}
	});

	function processSvgCurrentColor(content: string): string {
		if (!useCurrentColor) {
			return content;
		}

		return content
			.replace(/fill="(?!none|transparent)[^"]*"/giu, 'fill="currentColor"')
			.replace(/stroke="(?!none|transparent)[^"]*"/giu, 'stroke="currentColor"')
			.replace(/fill:\s*(?!none|transparent)[^;]+;?/giu, "fill: currentColor;")
			.replace(/stroke:\s*(?!none|transparent)[^;]+;?/giu, "stroke: currentColor;");
	}

	// Load SVG content for processing (only for same-origin URLs)
	$effect(() => {
		if (isSvg() && useCurrentColor && !isExternalUrl()) {
			void fetch(imageUrl)
				.then((response) => response.text())
				.then((content) => {
					svgContent = processSvgCurrentColor(content);
					isLoaded = true;
				})
				.catch(() => {
					isLoaded = true;
				});
		} else {
			isLoaded = true;
		}
	});

	/* eslint-disable svelte/no-at-html-tags */
</script>

{#if isSvg() && useCurrentColor && !isExternalUrl() && isLoaded && svgContent}
	<div
		class={customClass}
		style="width: {width}px; height: {height}px; display: flex; align-items: center; justify-content: center;"
		role="img"
		aria-label={alt}
	>
		{@html svgContent}
	</div>
{:else if isSvg() && isExternalUrl() && isLoaded}
	<img
		src={imageUrl}
		{alt}
		{width}
		{height}
		class="{customClass} external-svg-white"
		style="filter: brightness(0) saturate(100%) invert(100%);"
		loading="lazy"
	/>
{:else if isLoaded}
	<img src={imageUrl} {alt} {width} {height} class={customClass} loading="lazy" />
{/if}
